import { useState, useEffect, useCallback, useMemo, type FC } from "react";
import { Modal } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import LoadingSpinner from "src/components/reusable/LoadingSpinner";
import { DefinitionTooltip } from "src/components/common";
import { CHART_TOOLTIP_CURSOR } from "src/components/charts/ChartPalette";
import { OrganicChartTooltip } from "src/components/common/OrganicChartTooltip/OrganicChartTooltip";
import { fetchCampaignStats, type CampaignStatsData } from "src/services/EngagementAPIs";
import { useTranslation } from "react-i18next";
import { formatNumber, wrapBidiIsolate } from "src/utils/numberFormat";
import { useDynamicTextTranslator } from "src/utils/dynamicTextTranslation";
import { getEngagementTooltipInfo } from "src/layouts/Engagements/Overview/Index";
import "../Overview/Overview.scss";

export type CampaignPerformanceProps = {
  open?: boolean;
  onClose?: () => void;
  campaignId?: string;
  campaignName?: string;
};

const DONUT_COLORS = {
  delivered: "#2b74e4",
  bounced: "#94d0b5",
} as const;

const BAR_COLOR = "#2b74e4";
const DUMMY_TOTAL_SENT = 100;
const DUMMY_DONUT_SLICES = [
  {
    key: "delivered",
    value: 69,
    color: DONUT_COLORS.delivered,
    swatchClass: "engagement-overview-donut__swatch--delivered",
  },
  {
    key: "bounced",
    value: 18,
    color: DONUT_COLORS.bounced,
    swatchClass: "engagement-overview-donut__swatch--bounced",
  },
] as const;
const DUMMY_BAR_VALUE = 1;
const DUMMY_PROGRESS_VALUES = {
  openRate: 89,
  clickRate: 56,
  conversionRate: 50,
  unsubscribeRate: 48,
  shareRate: 40,
} as const;

const CampaignPerformance: FC<CampaignPerformanceProps> = ({
  open,
  onClose,
  campaignId: campaignIdProp,
  campaignName: campaignNameProp,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const isModal = typeof onClose === "function";
  const locationState = (location.state || {}) as {
    campaign_id?: string;
    campaign_name?: string;
  };
  const campaign_id = campaignIdProp || locationState.campaign_id;
  const campaign_name = campaignNameProp || locationState.campaign_name;
  const campaignNamePool = useMemo(
    () => (campaign_name ? [campaign_name] : []),
    [campaign_name],
  );
  const translateDynamicLabel = useDynamicTextTranslator(
    campaignNamePool,
    i18n.language,
  );
  const displayCampaignName =
    translateDynamicLabel(campaign_name || "") ||
    campaign_name ||
    t("campaignFlow.campaignPerformancePage.fallbackCampaign");
  const [loading, setLoading] = useState<boolean>(true);
  const [campaignStats, setCampaignStats] = useState<CampaignStatsData | null>(
    null
  );

  const formatLocalizedNumber = useCallback(
    (value: number, options: Intl.NumberFormatOptions = {}) =>
      formatNumber(value, options, i18n.language),
    [i18n.language]
  );

  const formatPercent = useCallback(
    (value: number, fractionDigits = 0) => {
      const percentText = `${formatLocalizedNumber(value, {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      })}%`;
      return i18n.language?.startsWith("ar") ? wrapBidiIsolate(percentText) : percentText;
    },
    [formatLocalizedNumber, i18n.language]
  );

  useEffect(() => {
    if (!campaign_id || !campaign_name) {
      return;
    }
    if (isModal && open === false) {
      return;
    }

    const resolvedCampaignId = campaign_id;
    const resolvedCampaignName = campaign_name;

    let cancelled = false;
    const loadStats = async () => {
      setLoading(true);
      const stats = await fetchCampaignStats(
        resolvedCampaignId,
        resolvedCampaignName
      );
      if (!cancelled) {
        setCampaignStats(stats);
        setLoading(false);
      }
    };

    loadStats();
    return () => {
      cancelled = true;
    };
  }, [campaign_id, campaign_name, isModal, open]);

  const donutSlices = useMemo(
    () =>
      DUMMY_DONUT_SLICES.map(slice => ({
        name: t(`campaignFlow.campaignPerformancePage.${slice.key}`),
        value: slice.value,
        color: slice.color,
        percent: slice.value,
        swatchClass: slice.swatchClass,
      })),
    [t]
  );

  const barData = useMemo(
    () => [
      {
        name: t("campaignFlow.campaignPerformancePage.newUsers"),
        value: DUMMY_BAR_VALUE,
      },
      {
        name: t("campaignFlow.campaignPerformancePage.returningUsers"),
        value: DUMMY_BAR_VALUE,
      },
    ],
    [t]
  );

  const leftMetrics = useMemo(
    () => [
      {
        key: "openRate",
        label: t("campaignDashboard.openRate"),
        value: DUMMY_PROGRESS_VALUES.openRate,
        progressClass: "metric-progress metric-progress--open",
      },
      {
        key: "clickRate",
        label: t("campaignDashboard.clickRate"),
        value: DUMMY_PROGRESS_VALUES.clickRate,
        progressClass: "metric-progress metric-progress--click",
      },
      {
        key: "conversionRate",
        label: t("campaignDashboard.conversionRate"),
        value: DUMMY_PROGRESS_VALUES.conversionRate,
        progressClass: "metric-progress metric-progress--conversion",
      },
    ],
    [t]
  );

  const rightMetrics = useMemo(
    () => [
      {
        key: "unsubscribeRate",
        label: t("campaignFlow.campaignPerformancePage.unsubscribedRate"),
        value: DUMMY_PROGRESS_VALUES.unsubscribeRate,
        progressClass: "metric-progress metric-progress--unsubscribe",
      },
      {
        key: "shareRate",
        label: t("campaignFlow.campaignPerformancePage.emailSharingRate"),
        value: DUMMY_PROGRESS_VALUES.shareRate,
        progressClass: "metric-progress metric-progress--share",
      },
    ],
    [t]
  );

  const totalEmailsTitle = t("campaignFlow.campaignPerformancePage.totalEmailsSent");
  const totalUsersTitle = t("campaignFlow.campaignPerformancePage.totalUsers");
  const performanceTitle = t("campaignDashboard.campaignPerformance");
  const totalSent = DUMMY_TOTAL_SENT;

  const renderMetricColumn = (
    metrics: Array<{
      key: string;
      label: string;
      value: number;
      progressClass: string;
    }>
  ) => (
    <div className="metrics-card__column">
      {metrics.map(metric => (
        <div key={metric.key} className="metric-row">
          <div className="metric-label">{metric.label}</div>
          <div className="metric-row__body">
            <progress
              className={metric.progressClass}
              max={100}
              value={Math.min(100, Math.max(0, metric.value))}
              aria-label={metric.label}
            />
            <strong className="metric-value">{formatPercent(metric.value, 0)}</strong>
          </div>
        </div>
      ))}
    </div>
  );

  const content = loading ? (
    <LoadingSpinner />
  ) : (
    <div className={`campaign-performance ${isModal ? "campaign-performance--modal" : ""}`}>
      {!isModal && (
        <>
          <button
            type="button"
            className="button button--primary campaign-performance__back-btn"
            onClick={() => navigate("/account/engagement/campaign-analytics")}
          >
            <span>&nbsp;</span> {t("campaignFlow.campaignPerformancePage.goBack")}
          </button>
          <span className="headline campaign-performance__page-title">
            {t("campaignFlow.campaignPerformancePage.titlePrefix")}
            {"  "}
            <span className="chevron">&nbsp;&nbsp;&nbsp;</span>{" "}
            {displayCampaignName}{" "}
          </span>
        </>
      )}

      {isModal && (
        <div className="campaign-performance__modal-heading">
          <h2 className="campaign-performance__modal-title">{performanceTitle}</h2>
          <p className="campaign-performance__modal-subtitle">
            {displayCampaignName}
          </p>
        </div>
      )}

      <div className="performance-cards performance-cards--popup">
        <div className="performance-cards__top-row">
          <article className="card donut-card engagement-overview-panel engagement-overview-panel--donut">
            <div className="card-section-heading">
              <h3 className="card-section-title">{totalEmailsTitle}</h3>
              <DefinitionTooltip
                label={totalEmailsTitle}
                info={getEngagementTooltipInfo(t, "totalEmailsSent")}
                formulaTitle={t("engagementOverview.formula")}
              />
            </div>
            <div className="engagement-overview-donut campaign-performance__donut">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={donutSlices}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius="58%"
                    outerRadius="86%"
                    startAngle={90}
                    endAngle={-270}
                    paddingAngle={0}
                    labelLine={false}
                  >
                    {donutSlices.map(slice => (
                      <Cell key={slice.name} fill={slice.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    allowEscapeViewBox={{ x: true, y: true }}
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) {
                        return null;
                      }
                      const item = payload[0];
                      return (
                        <div className="organic-tooltip organic-tooltip--kpi">
                          <div className="organic-tooltip__bar" />
                          <div className="organic-tooltip__content">
                            <div className="organic-tooltip__meta-line">
                              <span className="organic-tooltip__meta-label">
                                {t("engagementOverview.segment")}:
                              </span>
                              <strong className="organic-tooltip__meta-value">
                                {String(item.name ?? "")}
                              </strong>
                            </div>
                            <div className="organic-tooltip__meta-line">
                              <span className="organic-tooltip__meta-label">
                                {t("engagementOverview.users")}:
                              </span>
                              <strong className="organic-tooltip__meta-value">
                                {formatLocalizedNumber(Number(item.value ?? 0))}
                              </strong>
                            </div>
                          </div>
                        </div>
                      );
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="engagement-overview-donut__center">
                <span className="engagement-overview-donut__center-value">
                  {formatLocalizedNumber(totalSent)}
                </span>
                <span className="engagement-overview-donut__center-label">
                  {t("campaignFlow.campaignPerformancePage.emailsSentCaption")}
                </span>
              </div>
            </div>
            <div
              className="engagement-overview-donut__legend engagement-overview-donut__legend--centered"
              aria-hidden
            >
              {donutSlices.map(item => (
                <div
                  key={item.name}
                  className="engagement-overview-donut__legend-item"
                >
                  <span
                    className={`engagement-overview-donut__swatch ${item.swatchClass}`}
                  />
                  <span className="engagement-overview-donut__legend-text">
                    {item.name}
                  </span>
                  <strong>{formatPercent(item.percent, 0)}</strong>
                </div>
              ))}
            </div>
          </article>

          <article className="card bar-card">
            <div className="card-section-heading">
              <h3 className="card-section-title">{totalUsersTitle}</h3>
              <DefinitionTooltip
                label={totalUsersTitle}
                info={getEngagementTooltipInfo(t, "campaignTotalUsers")}
                formulaTitle={t("engagementOverview.formula")}
              />
            </div>
            <div className="bar-chart campaign-performance__bar-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={barData}
                  barSize={48}
                  margin={{ top: 12, right: 8, left: 0, bottom: 4 }}
                  maxBarSize={48}
                >
                  <CartesianGrid stroke="#e8edf4" strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="name"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: "#60718b" }}
                  />
                  <YAxis
                    allowDecimals={false}
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 11, fill: "#60718b" }}
                    tickFormatter={value => formatLocalizedNumber(Number(value ?? 0))}
                    width={28}
                    domain={[0, 4]}
                    ticks={[0, 1, 2, 3, 4]}
                  />
                  <Tooltip
                    cursor={{ fill: CHART_TOOLTIP_CURSOR }}
                    content={({ active, label, payload }) => (
                      <OrganicChartTooltip
                        active={active}
                        label={label}
                        labelTitle={t("engagementOverview.category")}
                        payload={payload}
                        seriesLabelForDataKey={() => t("engagementOverview.users")}
                        valueFormatter={value =>
                          formatLocalizedNumber(Number(value ?? 0))
                        }
                      />
                    )}
                  />
                  <Bar dataKey="value" fill={BAR_COLOR} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </article>
        </div>

        <article className="card metrics-card metrics-card--grid">
          <div className="card-section-heading">
            <h3 className="card-section-title">{performanceTitle}</h3>
            <DefinitionTooltip
              label={performanceTitle}
              info={getEngagementTooltipInfo(t, "campaignPerformance")}
              formulaTitle={t("engagementOverview.formula")}
            />
          </div>
          <div className="metrics-card__grid">
            {renderMetricColumn(leftMetrics)}
            {renderMetricColumn(rightMetrics)}
          </div>
        </article>
      </div>

      <div className="action-buttons">
        <button
          type="button"
          className="button button--outline"
          onClick={() => {
            const resolvedId = campaign_id;
            const resolvedName =
              campaignStats?.campaign_name || campaign_name;
            if (!resolvedId || !resolvedName) {
              return;
            }
            navigate("/account/engagement/compare-campaigns", {
              state: {
                baseCampaign: {
                  id: resolvedId,
                  name: resolvedName,
                },
              },
            });
          }}
        >
          {t("campaignFlow.campaignPerformancePage.compare")}
        </button>
        {/* <button
          type="button"
          className="button button--primary"
          onClick={() =>
            navigate("/account/engagement/create-campaign", {
              state: {
                campaign_id: campaign_id,
                campaign_name: campaignStats?.campaign_name || campaign_name,
              },
            })
          }
        >
          {t("campaignFlow.campaignPerformancePage.sendAgain")}
        </button> */}
      </div>
    </div>
  );

  if (isModal) {
    return (
      <Modal
        className="campaign-performance-modal"
        open={Boolean(open)}
        onCancel={onClose}
        footer={null}
        centered
        destroyOnClose
        width={950}
      >
        {content}
      </Modal>
    );
  }

  return content;
};

export default CampaignPerformance;
